import { For, createRoot, createSignal, onCleanup, onMount } from "solid-js";
import { render } from "solid-js/web";

import { useI18n } from "@/i18n";
import { isMobile } from "@/lib/isMobile.ts";
import { getImgurl } from "@/lib/resFolder.ts";
import { cn } from "@/lib/tailwindcssTool";

import "./Toaster.css";

const { t } = useI18n();

type ToastType =
  | "Success"
  | "Error"
  | "Notice"
  | "Info"
  | "Withdraw"
  | "Publish";

const typeIconMap: Record<ToastType, string> = {
  Success: getImgurl("success.png", "toaster"),
  Error: getImgurl("error.png", "toaster"),
  Notice: getImgurl("warning.png", "toaster"),
  Publish: getImgurl("publish.png", "toaster"),
  Info: getImgurl("warning.png", "toaster"),
  Withdraw: getImgurl("withdraw.png", "toaster"),
};

interface ToastOptions {
  type?: ToastType;
  duration?: number;
}

interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

const baseToastTypes: ToastType[] = ["Success", "Error", "Notice"];
const colorToastTypes: ToastType[] = ["Publish"];
const [toasts, setToasts] = createSignal<ToastProps[]>([]);

function removeToast(id: string) {
  console.log("removeToast called for id:", id);
  setToasts((prev) => {
    const newToasts = prev.filter((toast) => toast.id !== id);
    console.log("Toasts after removal:", newToasts);
    return newToasts;
  });
}

// dismissible, position功能是否需要
const ToastComponent = (props: ToastProps) => {
  const [isOpen, setIsOpen] = createSignal(true);
  const [progress, setProgress] = createSignal(100);
  const isBaseToast = baseToastTypes.includes(props.type);
  const isColorToast = colorToastTypes.includes(props.type);

  const handleClose = () => {
    console.log("handleClose called for toast:", props.id);
    setIsOpen(false);
    setTimeout(() => {
      console.log("Removing toast:", props.id);
      removeToast(props.id);
    }, 500);
  };
  const typeStrMap: Record<ToastType, string> = {
    Success: t("common.success"),
    Error: t("common.error"),
    Notice: t("common.notice"),
    Publish: t("common.publish"),
    Info: t("common.info"),
    Withdraw: t("common.withdraw"),
  };

  onMount(() => {
    const duration = props.duration ?? 5000;
    if (duration <= 0) return;

    console.log("Setting up timer for toast:", props.id, "duration:", duration);

    const interval = 100;
    const steps = duration / interval;
    const decrementPerStep = 100 / steps;

    const updateProgress = () =>
      setProgress((prev) => Math.max(0, prev - decrementPerStep));

    const timer = setInterval(updateProgress, interval);
    const timeout = setTimeout(() => {
      console.log("Timer expired for toast:", props.id);
      // 直接调用 handleClose，确保倒计时结束后自动关闭
      handleClose();
    }, duration);

    onCleanup(() => {
      console.log("Cleaning up timer for toast:", props.id);
      clearInterval(timer);
      clearTimeout(timeout);
    });
  });

  return (
    <div
      class={`toast ${isMobile() ? "mobile-toast" : "pc-toast"}`}
      data-opened={isOpen() ? true : undefined}
      data-closed={!isOpen() ? true : undefined}
    >
      <div class="toast__content">
        <div class="toast__icon">
          <img
            src={typeIconMap[props.type]}
            alt={props.type}
            class={isColorToast ? "toast__icon-img-large" : "toast__icon-img"}
          />
        </div>
        <div
          class={
            isMobile() ? "mobile-toast__content-text" : "pc-toast__content-text"
          }
          onClick={handleClose}
        >
          <div
            class={cn(
              isMobile() ? "mobile-toast__title" : "pc-toast__title",
              isColorToast ? "toast__publish-title" : "",
            )}
          >
            {isBaseToast ? typeStrMap[props.type] : props.title}
          </div>
          {props.description && (
            <div
              class={
                isMobile()
                  ? "mobile-toast__description"
                  : "pc-toast__description"
              }
            >
              {props.description}
            </div>
          )}
        </div>
        <button
          class={
            isMobile() ? "mobile-toast__close-button" : "pc-toast__close-button"
          }
          onClick={handleClose}
        >
          ✕
        </button>
      </div>
      {isBaseToast && (
        <div class="toast__progress-track">
          <div
            class="toast__progress-fill"
            style={{ width: `${progress()}%` }}
          />
        </div>
      )}
    </div>
  );
};

let isRegionMounted = false;
let renderRoot: (() => void) | null = null;

function ensureToastRoot() {
  let toastRoot = document.getElementById("toast-root");

  const className = isMobile()
    ? "fixed z-[10000] flex flex-col gap-2 top-16 w-full items-center"
    : "fixed z-[10000] flex flex-col gap-2 top-16 right-[45px]";

  if (!toastRoot) {
    toastRoot = document.createElement("div");
    toastRoot.id = "toast-root";
    toastRoot.className = className;
    document.body.appendChild(toastRoot);
  } else {
    toastRoot.className = className;
  }

  return toastRoot;
}

function cleanupPreviousRoot() {
  if (renderRoot) {
    try {
      renderRoot();
    } catch (error) {
      console.warn("Error cleaning up previous root:", error);
    }
    renderRoot = null;
  }
}

function mountToastRegion() {
  cleanupPreviousRoot();
  isRegionMounted = false;

  try {
    const toastRoot = ensureToastRoot();

    renderRoot = createRoot((dispose) => {
      render(
        () => (
          <For each={toasts()}>{(toast) => <ToastComponent {...toast} />}</For>
        ),
        toastRoot,
      );
      return dispose;
    });

    isRegionMounted = true;
    return true;
  } catch (error) {
    console.error("Error mounting toast region:", error);
    return false;
  }
}

if (typeof window !== "undefined") {
  const mountToasts = () => {
    setTimeout(() => {
      ensureToastRoot();
      mountToastRegion();
    }, 100);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mountToasts);
  } else {
    mountToasts();
  }
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 5);
}

function getToast(type: ToastType, title: string, description?: string) {
  const toast = toasts().find((t) => {
    return (
      t.type === type && t.title === title && t.description === description
    );
  });

  return toast;
}

function show(
  type: ToastType,
  title: string,
  description?: string,
  options?: { duration?: number },
) {
  if (!isRegionMounted) {
    if (!mountToastRegion()) {
      console.error("Failed to mount toast region. Toast cannot be shown.");
      return "";
    }
  }

  const toast = getToast(type, title, description);
  if (toast) {
    return toast.id;
  }

  const toastId = generateId();

  setToasts((prev) => {
    const newToasts = [
      ...prev,
      {
        id: toastId,
        type,
        title,
        description,
        duration: options?.duration,
      },
    ];
    return newToasts;
  });

  return toastId;
}

export const toast = {
  show,
  success(description: string, options?: ToastOptions) {
    return show("Success", t("common.success"), description, options);
  },
  error(description: string, options?: ToastOptions) {
    return show("Error", t("common.error"), description, options);
  },
  notice(description: string, options?: ToastOptions) {
    return show("Notice", t("common.notice"), description, options);
  },
  publish(title: string, description?: string, options?: ToastOptions) {
    return show("Publish", title, description, options);
  },
  info(title: string, description?: string, options?: ToastOptions) {
    return show("Info", title, description, options);
  },
  withdraw(description: string, options?: ToastOptions) {
    return show("Withdraw", t("common.withdraw"), description, options);
  },
};
